import { useEffect, useRef } from "react";
import { useGuidedTour } from "@/lib/guidedTour";

const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const q = (tid) => document.querySelector(`[data-testid="${tid}"]`);

// Animated "ghost cursor" that glides to each element the tour presses, then ripples.
let cursorEl = null;
const ensureCursor = () => {
  if (cursorEl && document.body.contains(cursorEl)) return cursorEl;
  cursorEl = document.createElement("div");
  cursorEl.setAttribute("data-testid", "veroogle-tour-cursor");
  cursorEl.style.cssText = "position:fixed;left:50vw;top:60vh;z-index:9999;pointer-events:none;opacity:0;transition:left .55s cubic-bezier(.2,.8,.2,1),top .55s cubic-bezier(.2,.8,.2,1),opacity .25s;";
  cursorEl.innerHTML = '<svg width="26" height="26" viewBox="0 0 24 24" style="filter:drop-shadow(0 0 8px rgba(34,211,238,.9))"><path d="M4 2l16 9-7 2-3 7z" fill="#fff" stroke="#22d3ee" stroke-width="1.5" stroke-linejoin="round"/></svg>';
  document.body.appendChild(cursorEl);
  return cursorEl;
};
const ripple = (x, y) => {
  const r = document.createElement("div");
  r.setAttribute("data-testid", "veroogle-tour-ripple");
  r.style.cssText = `position:fixed;left:${x - 22}px;top:${y - 22}px;width:44px;height:44px;border-radius:9999px;border:2px solid rgba(34,211,238,.9);box-shadow:0 0 18px rgba(34,211,238,.6);z-index:9998;pointer-events:none;transform:scale(.3);opacity:1;transition:transform .6s ease-out,opacity .6s ease-out;`;
  document.body.appendChild(r);
  requestAnimationFrame(() => { r.style.transform = "scale(1.8)"; r.style.opacity = "0"; });
  setTimeout(() => r.remove(), 700);
};
const hideCursor = () => { if (cursorEl) cursorEl.style.opacity = "0"; };

const click = async (tid) => {
  const el = q(tid);
  if (!el || el.disabled) return false;
  el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  await wait(250);
  const b = el.getBoundingClientRect();
  const x = b.left + b.width / 2;
  const y = b.top + b.height / 2;
  const c = ensureCursor();
  c.style.opacity = "1";
  c.style.left = `${x - 4}px`;
  c.style.top = `${y - 2}px`;
  await wait(600);
  ripple(x, y);
  el.click();
  await wait(150);
  return true;
};

const waitFor = async (tid, timeout = 4000) => {
  const t0 = Date.now();
  while (Date.now() - t0 < timeout) {
    if (q(tid)) return true;
    await wait(120);
  }
  return false;
};

const openPhase = (n) => async () => {
  const t = q(`nds-accordion-toggle-phase${n}_veroogle`);
  if (t && t.getAttribute("aria-expanded") === "false") await click(`nds-accordion-toggle-phase${n}_veroogle`);
};
const runCard = (tid, delay = 1800) => async () => { await wait(delay); await click(tid); };

const ACTIONS = {
  "open-phase1": openPhase(1),
  "open-phase2": openPhase(2),
  "open-phase3": openPhase(3),
  "open-phase4": openPhase(4),
  "open-phase5": openPhase(5),
  "open-phase6": openPhase(6),
  scrape: async () => {
    await wait(900);
    await click("veroogle-t1010-scrape");
    if (!(await waitFor("veroogle-t1010-push-0", 5000))) return;
    for (let i = 0; i < 5; i += 1) await click(`veroogle-t1010-push-${i}`);
  },
  score: async () => {
    await wait(1500);
    for (let i = 0; i < 3; i += 1) await click(`veroogle-t1020-push-${i}`);
  },
  pipeline: async () => {
    await wait(1800);
    const first = document.querySelector('[data-testid^="veroogle-t1030-advance-"]');
    if (!first) return;
    const tid = first.getAttribute("data-testid");
    for (let i = 0; i < 2; i += 1) { await click(tid); await wait(700); }
    await click("veroogle-t1030-seq-email");
    await click("veroogle-t1030-send-touch");
  },
  statement: runCard("veroogle-t1040-sample", 1500),
  "run-t2500": runCard("veroogle-t2500-run", 2200),
  "run-t2220": runCard("veroogle-t2220-run"),
  "run-t2300": runCard("veroogle-t2300-run"),
  "run-t2400": runCard("veroogle-t2400-run", 1500),
  "run-t3100": runCard("veroogle-t3100-run", 2000),
  "run-t3200": runCard("veroogle-t3200-run"),
  "run-t3300": runCard("veroogle-t3300-run"),
  "run-t4100": runCard("veroogle-t4100-run"),
};
const resolveAction = (a) => ACTIONS[a] || (a.startsWith("run:") ? runCard(a.slice(4)) : null);

// Lives inside <GuidedTourProvider>; fires each beat's action once per tour run.
export default function TourActionRunner({ onReset }) {
  const { isActive, current } = useGuidedTour();
  const ran = useRef(new Set());
  const wasActive = useRef(false);

  useEffect(() => {
    if (isActive && !wasActive.current) {
      ran.current = new Set();
      onReset?.();
    }
    if (!isActive) hideCursor();
    wasActive.current = isActive;
  }, [isActive, onReset]);

  useEffect(() => {
    hideCursor();
    const action = isActive && current?.action;
    const fn = action && resolveAction(action);
    if (!fn || ran.current.has(current.id)) return;
    ran.current.add(current.id);
    fn().then(() => setTimeout(hideCursor, 900));
  }, [isActive, current]);

  return null;
}
